import {
  adaptPromotion,
  adaptPromotionEffect,
  isQualificationSatisfiedBySinglePack,
  mergePromotionsForRestaurant,
} from '@/utils/volume-promotions';
import { gql, useQuery } from '@apollo/client';
import {
  PromotionEffectMetadata,
  PromotionLevel,
  PromotionQualificationMetadata,
  RestaurantPromotion,
  VariantPackVolumePromotion,
  VolumePromotion,
} from '@/utils/volume-promotions-types';
import { useItemQueryVariables } from './use-item-query-variables';
import { useMemo } from 'react';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';
import {
  VolumePromotionsContext_GetPromotionsForRestaurantQuery,
  VolumePromotionsContext_GetPromotionsForRestaurantQueryVariables,
} from '@/types';

export interface VariantPackPromotionsMapType {
  [variantPackUUID: string]: VariantPackVolumePromotion[];
}
export interface PromotionWithVariantPacksMapType {
  [promotionUUID: string]: VolumePromotion;
}

export const GET_VOLUME_PROMOTIONS_FOR_RESTAURANT_QUERY = gql`
  query VolumePromotionsContext_GetPromotionsForRestaurant(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    getPromotionsForRestaurant(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      promotion {
        ...VolumePromotionsContext_VolumePromotionFields
      }
      promotion_effect {
        ...VolumePromotionsContext_PromotionEffectFields
      }
      promotion_qualification {
        ...VolumePromotionsContext_PromotionQualificationFields
      }
      variant_pack {
        ...ItemsContext_VariantsPackFields
      }
    }
  }

  fragment VolumePromotionsContext_VolumePromotionFields on promotions {
    background_color_end
    background_color_start
    background_image_url
    discount_type
    expires_at
    hide_marketing_tile
    hide_tagline
    marketing_text
    photo_url_list
    starts_at
    supplier {
      address {
        timezone
        uuid
      }
      uuid
    }
    tagline
    uuid
  }

  fragment VolumePromotionsContext_PromotionEffectFields on promotion_effects {
    metadata
    promotion_component_group_uuid
    uuid
  }

  fragment VolumePromotionsContext_PromotionQualificationFields on promotion_qualifications {
    metadata
    promotion_component_group_uuid
    uuid
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const useGetPromotionsForRestaurant = () => {
  const itemQueryVariables = useItemQueryVariables();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID,
  } = useUserContext();
  const { getVariantPackInfo } = useVariantPackInfoContext();
  const isOfflineModeActive = useOfflineModeActive();

  const { data, refetch } = useQuery<
    VolumePromotionsContext_GetPromotionsForRestaurantQuery,
    VolumePromotionsContext_GetPromotionsForRestaurantQueryVariables
  >(GET_VOLUME_PROMOTIONS_FOR_RESTAURANT_QUERY, {
    context: {
      noBatch: true,
    },
    skip: isOfflineModeActive,
    variables: {
      ...itemQueryVariables,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID: supplierUUID,
    },
  });

  const { restaurantPromotionsMap, variantPackPromotionsMap } = useMemo(() => {
    // Merge duplicate variant packs within the same promotion, taking into account
    // that each variant pack in a promotion can only be associated with one effect and/or qualification.
    // Duplicate responses are occurring because variant packs are more closely linked to effects or qualifications
    // rather than the promotion itself.
    const mergedPromotionVariantPacks = mergePromotionsForRestaurant(
      (data?.getPromotionsForRestaurant as RestaurantPromotion[]) || [],
    );

    return mergedPromotionVariantPacks.reduce<{
      restaurantPromotionsMap: PromotionWithVariantPacksMapType;
      variantPackPromotionsMap: VariantPackPromotionsMapType;
    }>(
      (acc, { promotion, promotion_component_groups }) => {
        if (promotion && promotion_component_groups?.length) {
          const adaptedPromotion = adaptPromotion(promotion);
          const promoUUID = promotion.uuid;

          if (!acc.restaurantPromotionsMap[promoUUID]) {
            // Treat order-level and No Discount effects as order-level so the promotion
            // renders even if all variant packs in pack-level effects aren't visible
            const hasOrderLevelEffect = promotion_component_groups.some(
              (cg) => {
                const effectMetadata = cg.promotion_effect?.metadata as
                  | PromotionEffectMetadata
                  | undefined;
                return (
                  !effectMetadata?.level ||
                  effectMetadata.level === PromotionLevel.Order
                );
              },
            );

            acc.restaurantPromotionsMap[promoUUID] = {
              ...adaptedPromotion,
              effectLevel: hasOrderLevelEffect
                ? PromotionLevel.Order
                : PromotionLevel.Pack,
              promotedVariantPacks: [],
            };
          }

          for (const componentGroup of promotion_component_groups) {
            const isSatisfiedBySinglePack =
              componentGroup.promotion_qualifications.every(
                isQualificationSatisfiedBySinglePack,
              );
            const qualificationMetadata =
              componentGroup.promotion_qualifications
                .map(
                  (qualification) =>
                    qualification.metadata as PromotionQualificationMetadata,
                )
                .find((metadata) => !metadata.discount_code);

            for (const variantPack of componentGroup.variant_packs) {
              const variantPackUUID = variantPack.uuid;
              const isEffectVP =
                componentGroup.promotion_effect_variant_pack_ids.has(
                  variantPackUUID,
                );
              const promotedVariantPack = adaptPromotionEffect(
                promotion,
                isEffectVP ? componentGroup.promotion_effect : null,
                variantPack,
              );

              // If the promotion price would have a higher price than its base price, then do not include the VariantPack
              if (isEffectVP) {
                const basePackPrice =
                  getVariantPackInfo(variantPackUUID)?.priceInMicros;
                const promoPackPrice = promotedVariantPack?.packPriceInMicros;
                if (
                  promoPackPrice &&
                  basePackPrice &&
                  promoPackPrice > basePackPrice
                ) {
                  continue;
                }
              }

              acc.restaurantPromotionsMap[promoUUID].promotedVariantPacks.push(
                promotedVariantPack,
              );

              if (!acc.variantPackPromotionsMap[variantPackUUID]) {
                acc.variantPackPromotionsMap[variantPackUUID] = [];
              }

              acc.variantPackPromotionsMap[variantPackUUID].push({
                ...adaptedPromotion,
                isSatisfiedBySinglePack,
                minPacks: qualificationMetadata?.min_packs,
                promotedVariantPackInfo: promotedVariantPack,
              });
            }
          }
        }

        return acc;
      },
      {
        restaurantPromotionsMap: {},
        variantPackPromotionsMap: {},
      },
    );
  }, [data, getVariantPackInfo]);

  const promotions: VolumePromotion[] = useMemo(() => {
    if (!data?.getPromotionsForRestaurant || isOfflineModeActive) {
      return [];
    }

    return Object.values(restaurantPromotionsMap).filter(
      (promotion) =>
        promotion.effectLevel !== PromotionLevel.Pack ||
        promotion.promotedVariantPacks.length,
    );
  }, [
    data?.getPromotionsForRestaurant,
    isOfflineModeActive,
    restaurantPromotionsMap,
  ]);

  return {
    data,
    promotions,
    refetch,
    variantPackPromotionsMap,
  };
};
